import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    Modal,
    Form,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Label,
    Input,
    FormFeedback,
    Row,
} from "reactstrap";
import DeleteModal from "../Common/DeleteModal";
import TableContainer from "../Common/TableContainer";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getBozorPayPaymentsQuery } from "../../queries";
import { addBozorPayPayments, deleteBozorPayPayments } from "../../api/user";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";

export function SellerBozorPay() {
    const { sellerId } = useParams();
    const [modal, setModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(null);
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const { data: data, isError, error } = useQuery({
        ...getBozorPayPaymentsQuery(sellerId),
    });

    useEffect(() => {
        if (isError) {
            alert(error?.response?.data?.error?.message || error?.message);
        }
    }, [isError, error]);

    const bozorPayService = data?.data?.data?.[0];
    const serviceId = bozorPayService?.serviceId;

    const addMutation = useMutation({
        mutationFn: (values) => addBozorPayPayments(sellerId, values),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["bozor-pay-payments", sellerId] });
            toggle();
            formik.resetForm();
        },
        onError: (err) => {
            alert(err?.response?.data?.error?.message || err?.message);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => deleteBozorPayPayments(sellerId, id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["bozor-pay-payments", sellerId] });
            setDeleteModal(null);
        },
        onError: (err) => {
            alert(err?.response?.data?.error?.message || err?.message);
        },
    });

    const toggle = () => setModal(!modal);

    const downloadQRCode = () => {
        const canvas = document.getElementById("qr-gen");
        if (canvas) {
            const pngUrl = canvas
                .toDataURL("image/png")
                .replace("image/png", "image/octet-stream");
            let downloadLink = document.createElement("a");
            downloadLink.href = pngUrl;
            downloadLink.download = `bozor-pay-qr-${sellerId}.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        }
    };

    const formik = useFormik({
        initialValues: {
            commission: "",
        },
        validationSchema: Yup.object({
            commission: Yup.number().required(t("Required")),
        }),
        onSubmit: (values) => addMutation.mutate(values),
    });

    const columns = useMemo(
        () => [
            {
                header: t("Commission"),
                accessorKey: "commission",
                enableColumnFilter: false,
                cell: (cell) => {
                    return `${cell.getValue()}%`;
                },
            },
            {
                header: t("Created At"),
                accessorKey: "createdAt",
                cell: (cell) => {
                    return new Date(cell.getValue()).toLocaleString();
                },
                enableColumnFilter: false,
            },
            {
                header: t("Actions"),
                cell: ({ row }) => (
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={downloadQRCode}
                        >
                            <i className="ri-qr-code-line"></i> {t("Download QR")}
                        </button>
                        <button
                            className="btn btn-danger btn-sm"
                            onClick={() => setDeleteModal(row.original.id)}
                        >
                            <i className="ri-delete-bin-6-fill"></i>
                        </button>
                    </div>
                ),
                enableColumnFilter: false,
            },
        ],
        [t]
    );

    return (
        <div className="card-body pt-0 pl-0" style={{ padding: "0" }}>
            <DeleteModal
                show={!!deleteModal}
                onDeleteClick={() => deleteMutation.mutate(deleteModal)}
                onCloseClick={() => setDeleteModal(null)}
            />
            <Row className="g-4 mb-3 align-items-center">
                <div className="col-sm-auto">
                    {data?.data?.data?.length === 0 && (
                        <button className="btn btn-success" onClick={toggle}>
                            <i className="ri-add-line me-1"></i> {t("create")}
                        </button>
                    )}
                </div>
            </Row>

            {data?.data?.data?.length > 0 ? (
                <TableContainer
                    columns={columns}
                    data={data?.data?.data || []}
                    customPageSize={10}
                />
            ) : (
                <div className="text-center p-3 text-muted">
                    <h5>{t("No Data")}</h5>
                </div>
            )}

            <Modal isOpen={modal} toggle={toggle} centered>
                <ModalHeader toggle={toggle}>{t("Add Bozor Pay")}</ModalHeader>
                <Form onSubmit={formik.handleSubmit}>
                    <ModalBody>
                        <div className="mb-3">
                            <Label className="form-label" htmlFor="commission">
                                {t("Commission")}
                            </Label>
                            <Input
                                id="commission"
                                name="commission"
                                type="number"
                                placeholder={t("Enter commission")}
                                value={formik.values.commission}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                invalid={formik.touched.commission && !!formik.errors.commission}
                            />
                            {formik.touched.commission && formik.errors.commission && (
                                <FormFeedback>{formik.errors.commission}</FormFeedback>
                            )}
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <button type="button" className="btn btn-light" onClick={toggle}>
                            {t("Close")}
                        </button>
                        <button type="submit" className="btn btn-success">
                            {t("create")}
                        </button>
                    </ModalFooter>
                </Form>
            </Modal>

            <div style={{ display: "none" }}>
                <QRCodeCanvas
                    id="qr-gen"
                    value={`{"serviceId": ${serviceId}}`}
                    size={512}
                    level={"H"}
                    includeMargin={true}
                />
            </div>

            <ToastContainer closeButton={false} limit={1} />
        </div>
    );
}
